import { useLayoutEffect } from 'react';

/**
 * Usage:
 * usePreventScroll(<level>, <boolean-condition>);
 *
 * `level` allows you to control whether the styles
 * are passed to html or body, whichever level makes more sense
 * for your application.
 **/
function usePreventScroll(level: 'body' | 'html', enabled?: boolean): void {
  const safeDocument: Document = document;

  useLayoutEffect(() => {
    const html = safeDocument?.documentElement;
    const body = safeDocument?.body;

    if (safeDocument == undefined || !html || !body) {
      return;
    }

    if (enabled) {
      // Add styles if enabled
      const scrollBarWidth = window.innerWidth - html.clientWidth;
      const bodyPaddingRight =
        parseInt(
          window.getComputedStyle(body).getPropertyValue('padding-right'),
          /* Force radixValue to be base-10 system to avoid errors:
           * https://stackoverflow.com/questions/6611824/why-do-we-need-to-use-radix-parameter-when-calling-parseint
           */
          10
        ) || 0;
      /**
       * [1] iOS and desktop Safari bug: Requires position to make `overflow: hidden`
       * prevent scrolling.
       * [2] Desktop Safari bug: Must override overflow in both directions
       * since `overflowY` won't prevent scroll if `overflowX` is applied.
       * [3] paddingRight takes into account scrollbars by running a check.
       **/

      switch (level) {
        case 'html': {
          html.style.position = 'relative'; /* [1] */
          html.style.overflow = 'hidden'; /* [2] */
          body.style.paddingRight = `${
            bodyPaddingRight + scrollBarWidth
          }px`; /* [3] */
          break;
        }
        case 'body': {
          body.style.setProperty('position', 'relative'); /* [1] */
          body.style.setProperty('overflow', 'hidden'); /* [2] */
          body.style.setProperty(
            'padding-right',
            `${bodyPaddingRight + scrollBarWidth}px`
          ); /* [3] */
          break;
        }

        default:
          return;
      }
    }

    return () => {
      switch (level) {
        case 'html': {
          // TODO(LNS-221): Preserve previous style states rather than resetting to empty values for more robust utility
          html.style.position = ''; /* [1] */
          html.style.overflow = ''; /* [2] */
          body.style.paddingRight = ''; /* [3] */
          break;
        }
        case 'body': {
          body.style.removeProperty('position'); /* [1] */
          body.style.removeProperty('overflow'); /* [2] */
          body.style.removeProperty('padding-right'); /* [3] */
          break;
        }

        default:
          return;
      }
    };
    // TODO(LNS-215): Clear styles on return to make utility more extendable for other uses
  }, [safeDocument, enabled, level]);
}

export default usePreventScroll;
